"use client";

import { useCallback, useEffect, useRef, type RefObject } from "react";
import { safeSessionStorage } from "@/lib/storage/safe-storage";
import { isUnknownRecord } from "@shared/validation/narrow";
import { consumesKey } from "./use-row-open";
import { TABLE_PARAM_KEYS } from "../state/use-record-url-state";

interface StoredGridScroll {
  search: string;
  top: number;
}

type PendingScroll = { current: StoredGridScroll | null };

const PRESENTATION_PARAM_KEYS = new Set(["cols", "hide", "layout"]);

const RESTORE_WINDOW_MS = 4_000;
const SCROLL_SETTLE_MS = 600;

const SCROLLING_KEYS = new Set([
  "ArrowUp",
  "ArrowDown",
  "ArrowLeft",
  "ArrowRight",
  "PageUp",
  "PageDown",
  "Home",
  "End",
  " ",
]);

export function recordGridScrollStorageKey(
  pathname: string,
  objectId: string,
  namespace?: string,
): string {
  return `record-grid-scroll:${pathname}:${namespace ?? objectId}`;
}

export function resultScopeSearch(
  search: string,
  namespace: string | undefined,
  accumulatesRows: boolean,
  extraKeys: string[] = [],
): string {
  const present = new URLSearchParams(search);
  const scope = new URLSearchParams();
  const owned = TABLE_PARAM_KEYS.filter(
    (name) =>
      !PRESENTATION_PARAM_KEYS.has(name) &&
      !(accumulatesRows && name === "page"),
  ).map((name) => (namespace ? `${namespace}-${name}` : name));
  for (const key of [...owned, ...extraKeys]) {
    const value = present.get(key);
    if (value !== null) scope.set(key, value);
  }
  return scope.toString();
}

function readStoredScroll(key: string): StoredGridScroll | null {
  const raw = safeSessionStorage().getItem(key);
  if (raw === null) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!isUnknownRecord(parsed)) return null;
    const { search, top } = parsed;
    if (typeof search !== "string" || typeof top !== "number") return null;
    return { search, top };
  } catch {
    return null;
  }
}

function reachedStoredScroll(
  element: HTMLElement,
  target: StoredGridScroll,
): boolean {
  const furthest = Math.max(0, element.scrollHeight - element.clientHeight);
  if (furthest === 0) return false;
  element.scrollTop = Math.min(target.top, furthest);
  return element.scrollTop >= target.top;
}

function trackUserScrollIntent(
  element: HTMLElement,
  onIntent: () => void,
  onReaderLeavesScrolling: () => void,
): () => void {
  const fromPointerDown = (event: Event) => {
    if (event.target === element) onIntent();
    else onReaderLeavesScrolling();
  };
  const fromPointerMove = (event: Event) => {
    if (event.target === element) onIntent();
  };
  const fromKey = (event: KeyboardEvent) => {
    const scrollsTheGrid =
      !(event.altKey || event.ctrlKey || event.metaKey) &&
      event.target instanceof Node &&
      element.contains(event.target) &&
      !consumesKey(event.target, element, event.key) &&
      SCROLLING_KEYS.has(event.key);
    if (scrollsTheGrid) onIntent();
    else onReaderLeavesScrolling();
  };
  element.addEventListener("wheel", onIntent, { passive: true });
  element.addEventListener("touchmove", onIntent, { passive: true });
  element.addEventListener("pointermove", fromPointerMove);
  document.addEventListener("pointerdown", fromPointerDown, true);
  document.addEventListener("keydown", fromKey, true);
  return () => {
    element.removeEventListener("wheel", onIntent);
    element.removeEventListener("touchmove", onIntent);
    element.removeEventListener("pointermove", fromPointerMove);
    document.removeEventListener("pointerdown", fromPointerDown, true);
    document.removeEventListener("keydown", fromKey, true);
  };
}

function watchScroll(
  element: HTMLElement,
  pending: PendingScroll,
  storageKey: () => string,
  scopeSearch: () => string | null,
): () => void {
  let readerIsScrolling = false;
  const stopTracking = trackUserScrollIntent(
    element,
    () => {
      readerIsScrolling = true;
      pending.current = null;
    },
    () => {
      readerIsScrolling = false;
    },
  );
  const onScroll = () => {
    const search = scopeSearch();
    if (!readerIsScrolling || search === null) return;
    safeSessionStorage().setItem(
      storageKey(),
      JSON.stringify({
        search,
        top: element.scrollTop,
      } satisfies StoredGridScroll),
    );
  };
  element.addEventListener("scroll", onScroll, { passive: true });
  return () => {
    stopTracking();
    element.removeEventListener("scroll", onScroll);
  };
}

function replayScroll(
  element: HTMLElement,
  pending: PendingScroll,
  expiresAt: { current: number },
): () => void {
  let frame = 0;
  let lastHeight = -1;
  let heightSettledAt = 0;
  const attempt = () => {
    const target = pending.current;
    if (!target) return;
    const now = Date.now();
    if (now > expiresAt.current) {
      pending.current = null;
      return;
    }
    if (reachedStoredScroll(element, target)) {
      pending.current = null;
      return;
    }
    if (element.scrollHeight !== lastHeight) {
      lastHeight = element.scrollHeight;
      heightSettledAt = now;
      expiresAt.current = now + RESTORE_WINDOW_MS;
    } else if (now - heightSettledAt > SCROLL_SETTLE_MS) {
      return;
    }
    frame = window.requestAnimationFrame(attempt);
  };
  attempt();
  return () => {
    if (frame !== 0) window.cancelAnimationFrame(frame);
  };
}

function currentScope(
  namespace: string | undefined,
  accumulatesRows: boolean,
  legacyKeys: string[] | undefined,
  scopeKey: string | undefined,
): string {
  const search = resultScopeSearch(
    window.location.search,
    namespace,
    accumulatesRows,
    legacyKeys,
  );
  return [search, scopeKey].filter(Boolean).join("|");
}

function useScopeSearch(
  object: ScrollableObject,
  scope: GridScrollScope,
): () => string | null {
  const { accumulatesRows, scopeKey } = scope;
  const searchSettled = scope.search === scope.querySearch;
  const { urlNamespace } = object;
  const legacyKeys = object.legacyUrl?.paramKeys;
  return useCallback(
    () =>
      searchSettled
        ? currentScope(urlNamespace, accumulatesRows, legacyKeys, scopeKey)
        : null,
    [urlNamespace, accumulatesRows, legacyKeys, scopeKey, searchSettled],
  );
}

interface ScrollableObject {
  id: string;
  urlNamespace?: string;
  legacyUrl?: { paramKeys: string[] };
}

interface GridScrollScope {
  accumulatesRows: boolean;
  syncsUrl: boolean;
  scopeKey: string | undefined;
  search: string;
  querySearch: string;
}

export function useGridScrollRestoration(
  scrollRef: RefObject<HTMLDivElement | null>,
  object: ScrollableObject,
  rowCount: number,
  scope: GridScrollScope,
): void {
  const { syncsUrl } = scope;
  const pending = useRef<StoredGridScroll | null>(null);
  const expiresAt = useRef(0);
  const { id, urlNamespace } = object;
  const storageKey = useCallback(
    () =>
      recordGridScrollStorageKey(window.location.pathname, id, urlNamespace),
    [id, urlNamespace],
  );
  const scopeSearch = useScopeSearch(object, scope);

  useEffect(() => {
    if (!syncsUrl) return;
    pending.current = readStoredScroll(storageKey());
    expiresAt.current = Date.now() + RESTORE_WINDOW_MS;
  }, [storageKey, syncsUrl]);

  useEffect(() => {
    const element = scrollRef.current;
    if (!element || !syncsUrl) return;
    return watchScroll(element, pending, storageKey, scopeSearch);
  }, [scrollRef, storageKey, scopeSearch, syncsUrl]);

  useEffect(() => {
    const element = scrollRef.current;
    const target = pending.current;
    if (!element || target === null || rowCount === 0) return;
    if (target.search !== scopeSearch()) {
      pending.current = null;
      return;
    }
    return replayScroll(element, pending, expiresAt);
  }, [rowCount, scrollRef, scopeSearch]);
}
